import { useState } from 'react'

import { create } from '@bufbuild/protobuf'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'
import type { MarketOrderPolicyEntry } from '@ebalance/proto/bffuser/v1/bid_limit_model_pb'
import { UpdateMarketOrderPolicyRequestSchema } from '@ebalance/proto/bffuser/v1/bid_limit_service_pb'
import { flushSync } from 'react-dom'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Flex,
  Grid,
  Switch,
  TertiaryButton,
  Text,
  type ToastType,
  toaster,
} from '@enechain/ecloud-designsystem'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { useWorkflowLockReasons } from '~/hooks/workflowCase/useWorkflowLockReasons'
import { useUpdateMarketOrderPolicy } from '~/pages/AdminBidLimits/hooks/useBidLimitSettings'
import {
  FIRST_SAVE_EXPECTED_UPDATED_AT,
  MARKET_AREAS,
} from '~/pages/AdminBidLimits/models/constants'

// chakra v3 / ark-ui の d.ts 解決問題 (worktree で @ark-ui/react 未 install) により
// toaster が `CreateToasterReturn` → `any` に collapse する。 使用する create の
// 最小シグネチャへ cast して型を復元する (= `as any` ではなく proper 型 cast)。
type ToasterCreate = {
  create: (options: { type?: ToastType; title?: string }) => unknown
}
const typedToaster = toaster as ToasterCreate

type ServerVersion = 'unsaved' | `${bigint}:${number}`

type SavedPolicy = {
  allowed: boolean
  version: ServerVersion
  updatedAt: Timestamp | undefined
}

type AcceptedIntent = {
  phase: 'accepted'
  requested: boolean
  basis: ServerVersion
}

type SaveIntent =
  | {
      phase: 'saving'
      requested: boolean
      basis: ServerVersion
      previous: AcceptedIntent | undefined
    }
  | AcceptedIntent

type SaveOutcome = 'saved' | 'failed'

const versionOf = (updatedAt: Timestamp | undefined): ServerVersion =>
  updatedAt === undefined
    ? 'unsaved'
    : `${updatedAt.seconds}:${updatedAt.nanos}`

const parseSaved = (
  entries: MarketOrderPolicyEntry[],
  segment: BidSegment,
): ReadonlyMap<number, SavedPolicy> => {
  const saved = new Map<number, SavedPolicy>()
  for (const entry of entries) {
    const areaCode = entry.areaCode as number
    if (entry.bidSegment !== segment || saved.has(areaCode)) continue
    saved.set(areaCode, {
      allowed: entry.allowed,
      version: versionOf(entry.updatedAt),
      updatedAt: entry.updatedAt,
    })
  }
  return saved
}

const shownAllowed = (
  saved: SavedPolicy | undefined,
  intent: SaveIntent | undefined,
): boolean =>
  intent !== undefined && intent.basis === (saved?.version ?? 'unsaved')
    ? intent.requested
    : (saved?.allowed ?? false)

const withIntent = (
  intents: ReadonlyMap<number, SaveIntent>,
  areaCode: number,
  intent: SaveIntent | undefined,
): ReadonlyMap<number, SaveIntent> => {
  const next = new Map(intents)
  if (intent === undefined) {
    next.delete(areaCode)
  } else {
    next.set(areaCode, intent)
  }
  return next
}

function useAreaSaves(
  entries: MarketOrderPolicyEntry[],
  segment: BidSegment,
): {
  shown: (areaCode: number) => boolean
  isSaving: boolean
  save: (
    areaCode: number,
    requested: boolean,
    send: (expectedUpdatedAt: Timestamp) => Promise<unknown>,
  ) => Promise<SaveOutcome>
} {
  const saved = parseSaved(entries, segment)
  const [intents, setIntents] = useState<ReadonlyMap<number, SaveIntent>>(
    () => new Map(),
  )

  const save = async (
    areaCode: number,
    requested: boolean,
    send: (expectedUpdatedAt: Timestamp) => Promise<unknown>,
  ): Promise<SaveOutcome> => {
    const policy = saved.get(areaCode)
    const basis = policy?.version ?? 'unsaved'
    const current = intents.get(areaCode)
    const previous = current?.phase === 'accepted' ? current : undefined
    // zag は hidden input に defaultChecked を渡し、 controlled 値が commit 間で
    // 変わった時だけ書き戻す。 失敗と同じ commit にまとまらないよう先に単独で commit する。
    flushSync(() => {
      setIntents((now) =>
        withIntent(now, areaCode, {
          phase: 'saving',
          requested,
          basis,
          previous,
        }),
      )
    })
    try {
      // 初回保存 (entry 未取得) は epoch 番兵を送る (= backend が None 扱い)。
      await send(policy?.updatedAt ?? FIRST_SAVE_EXPECTED_UPDATED_AT)
    } catch {
      setIntents((now) => withIntent(now, areaCode, previous))
      return 'failed'
    }
    setIntents((now) =>
      withIntent(now, areaCode, { phase: 'accepted', requested, basis }),
    )
    return 'saved'
  }

  return {
    shown: (areaCode) =>
      shownAllowed(saved.get(areaCode), intents.get(areaCode)),
    isSaving: [...intents.values()].some((intent) => intent.phase === 'saving'),
    save,
  }
}

type Props = {
  entries: MarketOrderPolicyEntry[]
  segment: BidSegment.SPOT_NORMAL | BidSegment.INTRADAY
  /** i18n key for the description text. Defaults to the spot-normal description. */
  descriptionKey?: string
  /**
   * workflow-back-bar-readonly AC-17: ワークフローケースが完了済み/削除済みのとき true。
   * true のとき一括ON/OFFトグル・エリアトグルを disabled にする。
   */
  isReadonly?: boolean
}

export const MarketOrderPolicyCard = ({
  entries,
  segment,
  descriptionKey = 'page:adminBidLimits.marketOrder.description',
  isReadonly = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { readonlyReason } = useWorkflowLockReasons()
  const { mutateAsync } = useUpdateMarketOrderPolicy()
  const [isBulkPending, setIsBulkPending] = useState(false)
  const saves = useAreaSaves(entries, segment)

  const getEntry = (areaCode: number): MarketOrderPolicyEntry | undefined =>
    entries.find(
      (e) => e.bidSegment === segment && (e.areaCode as number) === areaCode,
    )

  const handleToggle = async (
    areaCode: number,
    requested: boolean,
  ): Promise<void> => {
    const outcome = await saves.save(areaCode, requested, (expectedUpdatedAt) =>
      mutateAsync(
        create(UpdateMarketOrderPolicyRequestSchema, {
          bidSegment: segment,
          areaCode,
          allowed: requested,
          expectedUpdatedAt,
        }),
      ),
    )
    if (outcome === 'saved') {
      // **即時保存にも成功 toast を出す** (2026-09-02 デザイナー指摘)。
      // マトリクスは明示的な「保存」ボタンなので押した瞬間が保存の合図になるが、
      // この card はトグルを倒した時点で即保存されるため、**成功したことを伝える
      // 手掛かりが画面上に何も無かった** (失敗時だけ toast が出る非対称だった)。
      typedToaster.create({
        type: 'success',
        title: t('page:adminBidLimits.marketOrder.saveSuccess'),
      })
    } else {
      typedToaster.create({
        type: 'error',
        title: t('page:adminBidLimits.marketOrder.saveError'),
      })
    }
  }

  const handleBulkToggle = async (newAllowed: boolean): Promise<void> => {
    setIsBulkPending(true)
    try {
      const results = await Promise.allSettled(
        MARKET_AREAS.map((area) => {
          const entry = getEntry(area.code)
          return mutateAsync(
            create(UpdateMarketOrderPolicyRequestSchema, {
              bidSegment: segment,
              areaCode: area.code,
              allowed: newAllowed,
              // 初回保存 (entry 未取得) は epoch 番兵を送る (= backend が None 扱い)。
              expectedUpdatedAt:
                entry?.updatedAt ?? FIRST_SAVE_EXPECTED_UPDATED_AT,
            }),
          )
        }),
      )
      // 一括は `allSettled` なので**部分成功しうる**。1 件でも失敗したらエラーだけ出す
      // (成功と失敗の toast を 2 枚重ねない)。全件成功した時だけ成功 toast を出す。
      const hasFailure = results.some((r) => r.status === 'rejected')
      if (hasFailure) {
        typedToaster.create({
          type: 'error',
          title: t('page:adminBidLimits.marketOrder.saveError'),
        })
      } else {
        typedToaster.create({
          type: 'success',
          title: t('page:adminBidLimits.marketOrder.saveSuccess'),
        })
      }
    } finally {
      setIsBulkPending(false)
    }
  }

  const onAreas = MARKET_AREAS.filter((area) => saves.shown(area.code))
  const onCount = onAreas.length
  const total = MARKET_AREAS.length

  const summaryText = (): string => {
    if (onCount === 0) {
      return t('page:adminBidLimits.marketOrder.summaryAllOff')
    }
    if (onCount === total) {
      return t('page:adminBidLimits.marketOrder.summaryAllOn', { total })
    }
    return t('page:adminBidLimits.marketOrder.summaryPartial', {
      count: onCount,
      total,
      areas: onAreas.map((area) => t(area.nameKey)).join(' / '),
    })
  }

  const isDisabled = saves.isSaving || isBulkPending || isReadonly
  /**
   * 押せない理由。 design/CLAUDE.md「disabled 表示」に従い、 無効なだけで理由が
   * 出ない状態を作らない。 理由が無い (= 押せる) 時は `undefined` を返し、
   * `ButtonTooltip` 側で吹き出しごと出さない。
   */
  const disabledReason =
    readonlyReason ??
    (saves.isSaving || isBulkPending
      ? t('page:adminBidLimits.savingTooltip')
      : undefined)

  return (
    <Box>
      {/* 8080 のカード補足は 14px (md)。 12px (sm) だと同カード内の他の文字から
          沈んで読めない。 */}
      <Text marginBottom={3} color="Text.midEmphasis" fontSize="md">
        {t(descriptionKey)}
      </Text>
      {/* 一括ボタンは 8080 と同じ既定サイズ。 sm にすると同画面の他のボタンより
          小さく、 押せる面が痩せて見える。
          無効時の理由は包みの DS Tooltip で出す (UI 辞書 action-reason.md の区分 2。
          native の title は使わない)。 ボタンの名前を繰り返す Tooltip ではないので、
          「可視ラベルがある button には Tooltip を重ねない」 には
          当たらない (同じ画面の BidLimitMatrixCard の保存・編集と同じ形)。 */}
      <Flex gap={1} marginBottom={3}>
        <ButtonTooltip content={disabledReason}>
          <TertiaryButton
            disabled={isDisabled}
            onClick={() => void handleBulkToggle(true)}
          >
            {t('page:adminBidLimits.marketOrder.bulkAllOn')}
          </TertiaryButton>
        </ButtonTooltip>
        <ButtonTooltip content={disabledReason}>
          <TertiaryButton
            disabled={isDisabled}
            onClick={() => void handleBulkToggle(false)}
          >
            {t('page:adminBidLimits.marketOrder.bulkAllOff')}
          </TertiaryButton>
        </ButtonTooltip>
      </Flex>
      <Grid
        gridGap={2}
        gridTemplateColumns="repeat(auto-fill, minmax(140px, 1fr))"
      >
        {MARKET_AREAS.map((area) => {
          return (
            <Flex
              key={area.code}
              alignItems="center"
              gap="6px"
              padding="4px 6px"
              background="Surface.primary"
              borderWidth="1px"
              borderColor="Border.lowEmphasis"
              borderRadius="base"
            >
              {/* [DS制約] エリア名は兄弟の `<Text>` のまま。 DS が公開しているのは
                  `Switch.Root` だけ (default export = `ChakraSwitch.Root`) で
                  `Switch.Label` スロットが無く、 エリア名を children に移すと
                  **読み上げ名が空になる** (2026-09-02 実測: `getByRole('checkbox')`
                  が名前無しになる)。 ラベルを押しても切り替わらない点は残るが、
                  名前が消える方が実害が大きいので現状を維持する。
                  DS 側に `Switch.Label` が入ったら children へ移す。 */}
              <ButtonTooltip content={disabledReason}>
                <Switch
                  aria-label={`${t(area.nameKey)} ${t('page:adminBidLimits.marketOrder.label')}`}
                  checked={saves.shown(area.code)}
                  disabled={isDisabled}
                  onCheckedChange={(details: { checked: boolean }) => {
                    void handleToggle(area.code, details.checked)
                  }}
                  size="sm"
                />
              </ButtonTooltip>
              {/* design SoT: `.ecl-switch` = font-size md (14px)。 */}
              <Text fontSize="md">{t(area.nameKey)}</Text>
            </Flex>
          )
        })}
      </Grid>
      {/* 8080 は集計行を mid emphasis で描く。 low だと薄すぎて、 現在何エリアが
          ON なのかが読み取りづらい。 */}
      <Text marginTop={2} color="Text.midEmphasis" fontSize="sm">
        {summaryText()}
      </Text>
    </Box>
  )
}
